import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useApi } from 'lib/contexts/ApplicationState';
import { JobApplication } from 'lib/types/jobs';
import { ResumeButton } from 'components/ResumeButton';
import { LinkedIn } from '@mui/icons-material';
import { Avatar, Chip } from '@mui/material';
import { workTypes } from 'lib/constants/jobs';
import { CreatorsListUser } from 'lib/types/users';
import { CreatorPosts } from 'components/CreatorPosts';

interface Props {
  creator?: CreatorsListUser;
  id: number;
  onCancel?: () => void;
  isProspect?: boolean;
}

export const Application = ({
  creator, id, onCancel, isProspect,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [application, setApplication] = useState<JobApplication|undefined>();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const getApplication = async () => {
      try {
        const result = await API.getApplicationById(id);
        setApplication(result);
        setIsLoaded(true);
      } catch (error) {
        enqueueSnackbar(`Error loading data: ${error}`, {
          variant: 'error',
        });
      }
    };

    getApplication();
  }, [id]);

  if (!isLoaded) return <div>Loading...</div>;

  if (!application || !creator) {
    return <div>No application found.</div>;
  }

  return (
    <>
      <div className="flex gap-4 items-center">
        <h1 className="mb-2 flex gap-2 items-center">
          <Avatar src={creator.avatarUrl} alt={creator.name} />
          <div>
            <div className="text-lg font-bold">{creator.name}</div>
            <div className="text-xs">{creator.location?.name}</div>
          </div>
        </h1>
        <div className="flex text-mediumgray">
          {application.linkedinUrl && (
            <a
              href={application.linkedinUrl}
              className="underline font-bold"
              target="_blank"
              rel="noreferrer"
            >
              <LinkedIn fontSize="large" />
            </a>
          )}
        </div>
      </div>
      {application.resumeUrl && (
        <ResumeButton
          applicationId={application.id}
          name={creator.name}
          fontSize="large"
          showIcon={false}
          userId={creator.id}
        />
      )}
      {isProspect && (
        <div className="mb-4">
          <div>
            Apply as full-time or freelancer?
          </div>
          <div className="font-bold">
            {workTypes[application.workType || '']}
          </div>
        </div>
      )}
      {application.roles && application.roles.length > 0 && (
        <div className="mb-4">
          <div>
            Interested in these roles
          </div>
          <div className="flex gap-2 flex-wrap mt-2 sm:flex-nowrap">
            {application.roles?.map((role) => (
              <Chip
                clickable={false}
                label={role.name}
              />
            ))}
          </div>
        </div>
      )}
      <div className="mb-4">
        <div className="mb-2">
          Selected favorite posts
        </div>
        {application.posts?.length === 0 ? <div className="font-bold">None</div>
          : (
            <CreatorPosts
              posts={application.posts as any}
              size={240}
            />
          )}
      </div>
      {application.answers?.map((a) => (
        <div key={a.id} className="mb-4">
          <div>
            {a.question}
          </div>
          <div className="font-bold">
            {a.answer}
          </div>
        </div>
      ))}
      {onCancel && (
        <div className="w-full text-right">
          <button
            type="button"
            className="btn-primary"
            onClick={onCancel}
          >
            Close
          </button>
        </div>
      )}
    </>
  );
};
